import { useState, useMemo } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { SideModal } from './SideModal'
import { FiUploadCloud, FiDownload, FiLoader, FiCheckCircle, FiAlertTriangle, FiArrowLeft, FiArrowRight } from 'react-icons/fi'

// Importar Regras de Conciliação em massa (planilha/CSV) — pedido do financeiro pra
// subir uma lista nova e atualizada sem cadastrar uma-a-uma. Espelha o wizard de 4
// etapas do Extrato (ImportarExtratoCsv.tsx): upload → mapear colunas → conferir →
// concluir. Reusa /api/import/parse (mesmo parser genérico de XLS/XLSX/CSV) e envia
// pra /api/reconciliation-rules/import, que resolve categoria por NOME + direção.

interface ParseResp { success: boolean; grid: string[][]; suggestedHeader: number; error?: string }
type FieldKey = 'pattern' | 'kind' | 'category' | 'auto_create' | 'sort_order' | 'is_active'
type Row = { pattern?: string; kind?: string; category?: string; auto_create?: string; sort_order?: string; is_active?: string; __line?: number }

const FIELDS: { key: FieldKey; label: string; hints: string[]; required: boolean }[] = [
  { key: 'pattern',     label: 'Palavras-chave', hints: ['palavra', 'chave', 'padrao', 'padrão', 'pattern', 'descri'], required: true },
  { key: 'kind',        label: 'Direção',        hints: ['direcao', 'direção', 'kind', 'tipo'], required: true },
  { key: 'category',    label: 'Categoria',      hints: ['categoria', 'category'], required: false },
  { key: 'auto_create', label: 'Auto-criar',     hints: ['auto', 'auto-criar', 'auto_create', 'automatic'], required: false },
  { key: 'sort_order',  label: 'Ordem',          hints: ['ordem', 'prioridade', 'sort'], required: false },
  { key: 'is_active',   label: 'Ativa',          hints: ['ativa', 'active', 'status'], required: false },
]
const norm = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim()

function downloadModelo() {
  const csv = '﻿' + [
    ['Palavras-chave', 'Direção', 'Categoria', 'Auto-criar', 'Ordem', 'Ativa'],
    ['tarifa manutencao, tarifa conta', 'Débito', 'Tarifas Bancárias', 'sim', '0', 'sim'],
    ['pix recebido', 'Crédito', 'Vendas', 'não', '0', 'sim'],
  ].map(r => r.map(c => `"${c}"`).join(';')).join('\n')
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
  const a = document.createElement('a'); a.href = url; a.download = 'modelo-regras-conciliacao.csv'; a.click()
  URL.revokeObjectURL(url)
}

export function ImportarRegrasCsv({ onClose, onImported }: { onClose: () => void; onImported: () => void }) {
  const { authenticatedFetch } = useAuth()
  const [step, setStep] = useState(1)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fileName, setFileName] = useState('')
  const [grid, setGrid] = useState<string[][]>([])
  const [headerRow, setHeaderRow] = useState(0)
  const [mapping, setMapping] = useState<Record<string, number>>({})
  const [result, setResult] = useState<{ inserted: number; failed: { line: number | string; error: string }[]; avisos: { line: number; aviso: string }[] } | null>(null)

  const headerCells = grid[headerRow] || []
  const dataRows = useMemo(() => grid.slice(headerRow + 1).filter(r => r.some(c => String(c).trim() !== '')), [grid, headerRow])
  const maxCols = useMemo(() => grid.reduce((m, r) => Math.max(m, r.length), 0), [grid])

  const autoMap = (hdr: string[]) => {
    const m: Record<string, number> = {}
    FIELDS.forEach(f => { m[f.key] = hdr.findIndex(c => { const n = norm(c); return f.hints.some(h => n.includes(h)) }) })
    setMapping(m)
  }

  async function handleFile(file: File) {
    setBusy(true); setError(null)
    try {
      const fd = new FormData(); fd.append('file', file)
      const r = await authenticatedFetch('/api/import/parse', { method: 'POST', body: fd })
      const d: ParseResp = await r.json()
      if (!d.success) { setError(d.error || 'Falha ao ler o arquivo'); return }
      setFileName(file.name); setGrid(d.grid); setHeaderRow(d.suggestedHeader)
      autoMap(d.grid[d.suggestedHeader] || [])
      setStep(2)
    } catch { setError('Erro ao enviar o arquivo') }
    finally { setBusy(false) }
  }

  const missing = FIELDS.filter(f => f.required && (mapping[f.key] == null || mapping[f.key] < 0))
  const buildRows = (): Row[] => dataRows.map(row => {
    const o: Row = {}
    FIELDS.forEach(f => { const ci = mapping[f.key]; if (ci != null && ci >= 0) o[f.key] = row[ci] ?? '' })
    const idx = grid.indexOf(row); o.__line = idx >= 0 ? idx + 1 : undefined
    return o
  })

  async function commit() {
    setBusy(true); setError(null)
    try {
      const r = await authenticatedFetch('/api/reconciliation-rules/import', {
        method: 'POST', body: JSON.stringify({ rows: buildRows() }),
      })
      const d = await r.json()
      if (!d.success) { setError(d.error || 'Falha ao importar'); return }
      setResult({ inserted: d.inserted, failed: d.failed || [], avisos: d.avisos || [] }); setStep(4)
      if (d.inserted > 0) onImported()
    } catch { setError('Erro ao importar regras') }
    finally { setBusy(false) }
  }

  const steps = ['Carregar', 'Mapear', 'Confirmar', 'Concluir']
  const footer = (
    <>
      {step > 1 && step < 4 && <button onClick={() => { setError(null); setStep(s => s - 1) }} disabled={busy} className="neu-convex inline-flex items-center gap-2 rounded-lg border border-border h-9 px-3 text-sm hover:bg-secondary disabled:opacity-40"><FiArrowLeft className="w-4 h-4" /> Voltar</button>}
      {step === 2 && <button onClick={() => { setError(null); setStep(3) }} disabled={busy || missing.length > 0} className="neu-relief inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground h-9 px-3 text-sm font-medium hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed">Continuar <FiArrowRight className="w-4 h-4" /></button>}
      {step === 3 && <button onClick={commit} disabled={busy || dataRows.length === 0} className="neu-relief inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground h-9 px-3 text-sm font-medium hover:opacity-90 disabled:opacity-40">{busy ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiCheckCircle className="w-4 h-4" />} Importar {dataRows.length} regra{dataRows.length === 1 ? '' : 's'}</button>}
      {step === 4 && <button onClick={onClose} className="neu-relief rounded-lg bg-primary text-primary-foreground h-9 px-4 text-sm font-medium hover:opacity-90">Fechar</button>}
    </>
  )

  return (
    <SideModal title="Importar regras de conciliação" subtitle={fileName || 'XLS, XLSX ou CSV'} icon={FiUploadCloud} onClose={onClose} maxWidth="max-w-2xl" footer={footer}>
      {/* PLANOS (21/10/2026): cada passo do assistente virou uma seção de papel
          (.pln-secao) com título 15px; selects e botões h-9, textos de apoio 13px. */}
      <div className="flex items-center gap-1.5 text-[13px]">
        {steps.map((s, i) => {
          const n = i + 1, done = n < step, cur = n === step
          return (<div key={s} className="flex items-center gap-1.5">
            <span className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold ${done ? 'bg-primary text-primary-foreground' : cur ? 'bg-primary/15 text-primary ring-1 ring-primary' : 'bg-secondary text-muted-foreground'}`}>{n}</span>
            <span className={cur ? 'text-foreground font-medium' : 'text-muted-foreground'}>{s}</span>
            {i < steps.length - 1 && <span className="w-4 h-px bg-border mx-0.5" />}
          </div>)
        })}
      </div>
      {error && <div className="text-sm text-red-500 flex items-center gap-2"><FiAlertTriangle className="w-4 h-4 shrink-0" />{error}</div>}

      {step === 1 && (
        <section className="pln-secao">
          <h4 className="pln-secao-titulo">Arquivo</h4>
          <button onClick={downloadModelo} className="inline-flex items-center gap-2 text-sm text-primary hover:underline"><FiDownload className="w-4 h-4" /> Baixar modelo de CSV</button>
          <label className="neu-well block border-2 border-dashed border-border rounded-xl p-8 text-center transition-colors cursor-pointer hover:border-primary/50 hover:bg-secondary/30">
            <input type="file" accept=".xls,.xlsx,.csv" className="hidden" disabled={busy} onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f) }} />
            {busy ? <FiLoader className="w-8 h-8 mx-auto animate-spin text-muted-foreground" /> : <FiUploadCloud className="w-8 h-8 mx-auto text-muted-foreground" />}
            <div className="mt-2 text-sm text-foreground font-medium">Clique para selecionar o arquivo</div>
            <div className="text-[13px] text-muted-foreground mt-1">Palavras-chave separadas por vírgula. Direção: débito, crédito ou ambos.</div>
          </label>
        </section>
      )}

      {step === 2 && (
        <section className="pln-secao">
          <h4 className="pln-secao-titulo">Mapear colunas</h4>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Linha de cabeçalho:</span>
            <select value={headerRow} onChange={e => { const h = Number(e.target.value); setHeaderRow(h); autoMap(grid[h] || []) }} className="neu-convex rounded-md border border-border bg-card h-9 px-2.5 text-sm">
              {grid.slice(0, 15).map((r, i) => <option key={i} value={i}>Linha {i + 1} — {r.filter(c => String(c).trim()).slice(0, 3).join(', ').slice(0, 40) || '(vazia)'}</option>)}
            </select>
          </div>
          <p className="text-[13px] text-muted-foreground">Relacione Palavras-chave e Direção (obrigatórios); Categoria, Auto-criar, Ordem e Ativa são opcionais.</p>
          <div className="space-y-2">
            {FIELDS.map(f => (
              <div key={f.key} className="flex items-center gap-3">
                <label className="text-sm w-32 shrink-0 text-foreground">{f.label}{f.required && <span className="text-red-500"> *</span>}</label>
                <select value={mapping[f.key] ?? -1} onChange={e => setMapping(m => ({ ...m, [f.key]: Number(e.target.value) }))}
                  className={`flex-1 rounded-md border bg-card h-9 px-2.5 text-sm ${f.required && (mapping[f.key] ?? -1) < 0 ? 'border-red-400' : 'border-border'}`}>
                  <option value={-1}>— ignorar —</option>
                  {Array.from({ length: maxCols }, (_, ci) => <option key={ci} value={ci}>{headerCells[ci]?.trim() || `Coluna ${ci + 1}`}</option>)}
                </select>
              </div>
            ))}
          </div>
          {missing.length > 0 && <div className="text-[13px] text-amber-600 dark:text-amber-400">Falta mapear: {missing.map(f => f.label).join(', ')}.</div>}
        </section>
      )}

      {step === 3 && (
        <section className="pln-secao">
          <h4 className="pln-secao-titulo">Conferir antes de importar</h4>
          <p className="text-sm text-muted-foreground">{dataRows.length} linha{dataRows.length === 1 ? '' : 's'} pronta{dataRows.length === 1 ? '' : 's'}. Confira antes de importar.</p>
          <div className="border border-border rounded-lg overflow-x-auto max-h-[46vh]">
            <table className="w-full text-[13px] min-w-[520px]">
              <thead className="sticky top-0 bg-card"><tr className="text-left text-muted-foreground border-b border-border">
                <th className="px-2.5 py-2 font-medium">Palavras-chave</th><th className="px-2.5 py-2 font-medium">Direção</th>
                <th className="px-2.5 py-2 font-medium">Categoria</th><th className="px-2.5 py-2 font-medium">Auto-criar</th>
              </tr></thead>
              <tbody>
                {buildRows().slice(0, 100).map((r, i) => (
                  <tr key={i} className="border-b border-border/50 last:border-0">
                    <td className="px-2.5 py-2 truncate max-w-[220px]">{r.pattern || '—'}</td>
                    <td className="px-2.5 py-2 whitespace-nowrap">{r.kind || '—'}</td>
                    <td className="px-2.5 py-2 truncate max-w-[160px]">{r.category || '—'}</td>
                    <td className="px-2.5 py-2 whitespace-nowrap">{r.auto_create || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {dataRows.length > 100 && <p className="text-[13px] text-muted-foreground">Mostrando as 100 primeiras de {dataRows.length}.</p>}
        </section>
      )}

      {step === 4 && result && (
        <section className="pln-secao">
          <h4 className="pln-secao-titulo">Resultado</h4>
          <div className="flex items-center gap-3">
            <FiCheckCircle className="w-8 h-8 text-primary shrink-0" />
            <div>
              <div className="text-lg font-semibold text-foreground">{result.inserted} regra{result.inserted === 1 ? '' : 's'} importada{result.inserted === 1 ? '' : 's'}</div>
              {result.failed.length > 0 && <div className="text-sm text-red-500">{result.failed.length} linha(s) com erro</div>}
            </div>
          </div>
          {result.failed.length > 0 && (
            <ul className="text-[13px] text-red-500 space-y-0.5 max-h-32 overflow-y-auto">
              {result.failed.map((f, i) => <li key={i}>Linha {f.line}: {f.error}</li>)}
            </ul>
          )}
          {result.avisos.length > 0 && (
            <>
              <p className="text-[13px] text-muted-foreground">Avisos (a regra foi criada mesmo assim):</p>
              <ul className="text-[13px] text-amber-600 dark:text-amber-400 space-y-0.5 max-h-32 overflow-y-auto">
                {result.avisos.map((a, i) => <li key={i}>Linha {a.line}: {a.aviso}</li>)}
              </ul>
            </>
          )}
        </section>
      )}
    </SideModal>
  )
}
